import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { etiquetaNivel } from '@/lib/nivelesUsuario';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Clock, Users, AlertTriangle, Building2, Check, X, Trash2, Shield, RefreshCw, Save, Loader2, Pencil, Play, History, ChevronDown } from 'lucide-react';
import HelpTip from '@/components/HelpTip';

const DAYS = [
  { key: 'lunes', label: 'Lun' }, { key: 'martes', label: 'Mar' },
  { key: 'miercoles', label: 'Mie' }, { key: 'jueves', label: 'Jue' },
  { key: 'viernes', label: 'Vie' }, { key: 'sabado', label: 'Sab' },
  { key: 'domingo', label: 'Dom' },
];
const SHIFTS = [
  { key: 'manana', label: 'Manana (9-14h)', color: 'indigo' },
  { key: 'tarde', label: 'Tarde (14-19h)', color: 'amber' },
  { key: 'custom', label: 'Personalizado', color: 'blue' },
];
const PERM_MODULES = [
  { key: 'properties', label: 'Propiedades' },
  { key: 'leads', label: 'Leads' },
  { key: 'opportunities', label: 'Oportunidades' },
  { key: 'team', label: 'Equipo' },
  { key: 'settings', label: 'Configuracion' },
];

function HierarchicalGroupsSection({ type, title, description, entityLabel, entities, groups, vendors, onAdd, onEdit, onDelete, onToggle }) {
  const filteredGroups = groups.filter(g => g.type === type);

  const getEntityName = (linked_id) => {
    const entity = entities.find(e => e.id === linked_id);
    return entity?.name || linked_id || '—';
  };

  return (
    <div className="space-y-2 pt-3 border-t border-white/5" data-testid={`section-${type}`}>
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-xs text-white/60">{title}</Label>
          {description && <p className="text-[9px] text-muted-foreground mt-0.5">{description}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onAdd(type)} className="text-[10px] text-teal-400 h-6" data-testid={`add-group-${type}`}>
          <Plus className="w-3 h-3 mr-1" /> Nuevo Grupo
        </Button>
      </div>
      {filteredGroups.length === 0 && (
        <p className="text-[10px] text-muted-foreground italic px-1">No hay grupos configurados. Crea uno para activar asignacion por {entityLabel.toLowerCase()}.</p>
      )}
      {filteredGroups.map(group => (
        <div key={group.id} className="p-3 rounded bg-black/20 border border-white/5 space-y-1" data-testid={`group-${group.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-white font-medium">{group.name}</p>
              <p className="text-[9px] text-muted-foreground">{entityLabel}: {getEntityName(group.linked_id)}</p>
              <p className="text-[9px] text-muted-foreground">{(group.vendor_ids || []).length} vendedor(es)</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={group.is_active}
                onCheckedChange={() => onToggle(group)}
                data-testid={`toggle-group-${group.id}`}
              />
              <Button variant="ghost" size="sm" onClick={() => onEdit(type, group)} className="h-6 w-6 p-0 text-white/50 hover:text-white" data-testid={`edit-group-${group.id}`}>
                <Pencil className="w-3 h-3" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(group.id)} className="h-6 w-6 p-0 text-red-400 hover:text-red-300" data-testid={`delete-group-${group.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          {!group.is_active && (
            <Badge variant="outline" className="text-[9px] border-yellow-500/30 text-yellow-400">Desactivado</Badge>
          )}
        </div>
      ))}
    </div>
  );
}

export default function GuardsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const allowedModules = user?.allowed_modules || user?.company?.allowed_modules || {};
  const industry = user?.industry || user?.company?.industry || 'real_estate';
  // E-R6d: redirect a /products eliminado. El motor v2 (resolve_assignment_v2)
  // ahora maneja Colegios, Desarrolladoras y otras verticales con specialty,
  // por lo que el Calendario de Asignaciones es relevante para todas.
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [week, setWeek] = useState('');
  const [members, setMembers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ agent_id: '', day: 'lunes', shift: 'manana', property_id: '', time_start: '09:00', time_end: '14:00', replicate_days: [] });
  const [permDialog, setPermDialog] = useState(false);
  const [permGuard, setPermGuard] = useState(null);
  const [permData, setPermData] = useState({});

  const load = useCallback(async () => {
    try {
      const [st, sh, tm, pr, cr] = await Promise.all([
        api.get('/guards/stats'),
        api.get('/guards/shifts' + (week ? `?week=${week}` : '')),
        api.get('/team').catch(() => ({ data: [] })),
        api.get('/properties').catch(() => ({ data: [] })),
        api.get('/guards/change-requests').catch(() => ({ data: { requests: [] } })),
      ]);
      setStats(st.data);
      setShifts(sh.data?.shifts || []);
      if (!week) setWeek(sh.data?.week || '');
      setMembers(Array.isArray(tm.data) ? tm.data : []);
      setProperties(Array.isArray(pr.data) ? pr.data : []);
      setChangeRequests(cr.data?.requests || []);
    } catch { }
  }, [week]);

  useEffect(() => { load(); }, [load]);

  const createShift = async () => {
    if (!form.agent_id) { toast.error('Selecciona un agente'); return; }
    try {
      await api.post('/guards/shifts', { ...form, week });
      toast.success('Turno asignado');
      setDialogOpen(false);
      setForm({ agent_id: '', day: 'lunes', shift: 'manana', property_id: '', time_start: '09:00', time_end: '14:00', replicate_days: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteShift = async (id) => {
    try { await api.delete(`/guards/shifts/${id}`); load(); } catch { toast.error('Error'); }
  };

  const handleChangeRequest = async (id, action) => {
    try {
      await api.put(`/guards/change-requests/${id}`, { action });
      toast.success(action === 'approve' ? 'Aprobado' : 'Rechazado');
      load();
    } catch { toast.error('Error'); }
  };

  const openPermissions = async (guardId) => {
    try {
      const r = await api.get(`/guards/permissions/${guardId}`);
      setPermGuard(r.data);
      setPermData(r.data.permissions || {});
      setPermDialog(true);
    } catch { toast.error('Error al cargar permisos'); }
  };

  const savePermissions = async () => {
    if (!permGuard) return;
    try {
      await api.put(`/guards/permissions/${permGuard.user_id}`, { permissions: permData });
      toast.success('Permisos actualizados');
      setPermDialog(false);
    } catch { toast.error('Error'); }
  };

  const getShiftsForSlot = (agentId, day) =>
    shifts.filter(s => s.agent_id === agentId && s.day === day);

  const getMemberName = (id) => members.find(m => m.id === id)?.name || '?';
  const getPropertyName = (id) => properties.find(p => p.id === id)?.title || id || '';

  const uniqueAgents = [...new Set(shifts.map(s => s.agent_id))];

  // ── Roulette State ─────────────────────────────────
  const [rouletteConfig, setRouletteConfig] = useState(null);
  const [rouletteLoading, setRouletteLoading] = useState(false);
  const [rouletteSaving, setRouletteSaving] = useState(false);
  const [rouletteOptions, setRouletteOptions] = useState({ zones: [], groups: [], vendors: [] });
  const [rouletteGroups, setRouletteGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupDialogType, setGroupDialogType] = useState('campaign');
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', linked_id: '', vendor_ids: [], is_active: true, max_leads_per_vendor: -1 });
  const [groupSaving, setGroupSaving] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [simForm, setSimForm] = useState({ lead_source_id: '', product_id: '', brand_id: '' });
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  const loadRouletteConfig = async () => {
    setRouletteLoading(true);
    try {
      const calls = [
        api.get('/roulette/config'),
        api.get('/roulette/options'),
        api.get('/roulette/groups').catch(() => ({ data: [] })),
      ];
      if (allowedModules.brands) calls.push(api.get('/brands').catch(() => ({ data: [] })));
      if (allowedModules.products) {
        calls.push(api.get('/products').catch(() => ({ data: [] })));
        calls.push(api.get('/lead-sources').catch(() => ({ data: [] })));
      }
      const results = await Promise.all(calls);
      setRouletteConfig(results[0].data);
      setRouletteOptions(results[1].data || { zones: [], groups: [], vendors: [] });
      setRouletteGroups(Array.isArray(results[2].data) ? results[2].data : []);
      let idx = 3;
      if (allowedModules.brands) { setBrands(Array.isArray(results[idx]?.data) ? results[idx].data : []); idx++; }
      if (allowedModules.products) {
        setProducts(Array.isArray(results[idx]?.data) ? results[idx].data : []); idx++;
        setLeadSources(Array.isArray(results[idx]?.data) ? results[idx].data : []);
      }
    } catch { /* ok */ } finally { setRouletteLoading(false); }
  };

  const loadAuditLogs = () => {
    api.get('/admin/audit-logs?action=roulette&limit=10').then(r => setAuditLogs((r.data?.logs || []).slice(0, 10))).catch(() => {});
    api.get('/admin/audit-logs?action=guard&limit=10').then(r => setAuditLogs(prev => [...prev, ...(r.data?.logs || []).slice(0, 10)].sort((a, b) => b.created_at?.localeCompare(a.created_at)).slice(0, 10))).catch(() => {});
  };

  const toggleRuleAgent = (ruleIdx, agentId, ruleType) => {
    const rules = [...(rouletteConfig?.[ruleType] || [])];
    const current = rules[ruleIdx]?.agents || [];
    rules[ruleIdx] = { ...rules[ruleIdx], agents: current.includes(agentId) ? current.filter(a => a !== agentId) : [...current, agentId] };
    setRouletteConfig({ ...rouletteConfig, [ruleType]: rules });
  };

  const saveRouletteConfig = async () => {
    if (!rouletteConfig) return;
    setRouletteSaving(true);
    try {
      await api.put('/roulette/config', rouletteConfig);
      toast.success('Configuracion de ruleta guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setRouletteSaving(false); }
  };

  const openGroupDialog = (type, groupToEdit = null) => {
    setGroupDialogType(type);
    setEditingGroup(groupToEdit);
    setGroupForm(groupToEdit
      ? { name: groupToEdit.name, linked_id: groupToEdit.linked_id, vendor_ids: groupToEdit.vendor_ids || [], is_active: groupToEdit.is_active, max_leads_per_vendor: groupToEdit.max_leads_per_vendor ?? -1 }
      : { name: '', linked_id: '', vendor_ids: [], is_active: true, max_leads_per_vendor: -1 }
    );
    setGroupDialog(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name) { toast.error('Ingresa un nombre para el grupo'); return; }
    setGroupSaving(true);
    try {
      const payload = { ...groupForm, type: groupDialogType };
      if (editingGroup) {
        await api.put(`/roulette/groups/${editingGroup.id}`, payload);
        toast.success('Grupo actualizado');
      } else {
        await api.post('/roulette/groups', payload);
        toast.success('Grupo creado');
      }
      setGroupDialog(false);
      const r = await api.get('/roulette/groups').catch(() => ({ data: [] }));
      setRouletteGroups(Array.isArray(r.data) ? r.data : []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar grupo'); }
    finally { setGroupSaving(false); }
  };

  const deleteGroup = async (groupId) => {
    try {
      await api.delete(`/roulette/groups/${groupId}`);
      toast.success('Grupo eliminado');
      setRouletteGroups(prev => prev.filter(g => g.id !== groupId));
    } catch { toast.error('Error al eliminar grupo'); }
  };

  const toggleGroupActive = async (group) => {
    try {
      await api.put(`/roulette/groups/${group.id}`, { is_active: !group.is_active });
      setRouletteGroups(prev => prev.map(g => g.id === group.id ? { ...g, is_active: !g.is_active } : g));
    } catch { toast.error('Error al actualizar grupo'); }
  };

  const runSimulation = async () => {
    setSimLoading(true);
    try {
      const r = await api.post('/roulette/simulate', simForm);
      setSimResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error en simulacion'); }
    finally { setSimLoading(false); }
  };

  const changeRoleLevel = async (sourceId, newLevel) => {
    try {
      await api.put(`/lead-sources/${sourceId}`, { role_level: newLevel });
      setLeadSources(prev => prev.map(s => s.id === sourceId ? { ...s, role_level: newLevel } : s));
      toast.success('Nivel de rol actualizado');
    } catch { toast.error('Error al actualizar nivel'); }
  };

  return (
    <div className="space-y-6" data-testid="guards-page">
      <div>
        <h2 className="text-2xl font-bold text-white tracking-tight">Guardias y Asignacion</h2>
        <p className="text-sm text-muted-foreground">Turnos semanales y reglas de asignacion de leads</p>
      </div>

      <Tabs defaultValue="guardias">
        <TabsList className="bg-black/30 border border-white/5">
          <TabsTrigger value="guardias" className="text-xs data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-400" data-testid="tab-guardias">
            <Clock className="w-3.5 h-3.5 mr-1" /> Roles de Guardia <HelpTip title="Roles de Guardia" content="Administra disponibilidad por día, propiedad, zona y reglas. Puedes replicar turnos a varios días para acelerar la operación." />
          </TabsTrigger>
          <TabsTrigger value="ruleta" className="text-xs data-[state=active]:bg-teal-600/20 data-[state=active]:text-teal-400" data-testid="tab-ruleta" onClick={() => { if (!rouletteConfig) loadRouletteConfig(); loadAuditLogs(); }}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Ruleta de Asignacion
          </TabsTrigger>
        </TabsList>

        <TabsContent value="guardias" className="space-y-6 mt-4">
      <div className="flex items-center justify-between">
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="assign-guard-btn">
            <Plus className="w-4 h-4 mr-2" /> Asignar Guardia
          </Button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2"><div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center"><Check className="w-5 h-5 text-emerald-500" /></div></div>
            <p className="text-2xl font-bold text-white">{stats.total_shifts}</p><p className="text-[10px] text-muted-foreground uppercase">Turnos Semana</p>
          </CardContent></Card>
          <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2"><div className="w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center"><Users className="w-5 h-5 text-indigo-500" /></div></div>
            <p className="text-2xl font-bold text-white">{stats.active_agents}</p><p className="text-[10px] text-muted-foreground uppercase">Asesores en Rol</p>
          </CardContent></Card>
          <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2"><div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-500" /></div>
            {stats.pending_requests > 0 && <Badge className="bg-amber-500 text-black text-[9px]">{stats.pending_requests}</Badge>}</div>
            <p className="text-2xl font-bold text-white">{stats.pending_requests}</p><p className="text-[10px] text-muted-foreground uppercase">Solicitudes Cambio</p>
          </CardContent></Card>
          <Card className="bg-card/50 border-white/5"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-2"><div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-blue-500" /></div></div>
            <p className="text-2xl font-bold text-white">{stats.properties_covered}</p><p className="text-[10px] text-muted-foreground uppercase">Propiedades Asignadas</p>
          </CardContent></Card>
        </div>
      )}

      {/* Guard Members with Permissions */}
      {isAdmin && members.filter(m => m.role === 'guard').length > 0 && (
        <Card className="bg-card/50 border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-white flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" /> Permisos por Guardia</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {members.filter(m => m.role === 'guard').map(g => (
              <div key={g.id} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`guard-perms-${g.id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center text-[10px] font-bold text-amber-400">{g.name?.charAt(0)}</div>
                  <div><p className="text-xs text-white">{g.name}</p><p className="text-[10px] text-muted-foreground">{g.email}</p></div>
                </div>
                <Button variant="outline" size="sm" className="text-[10px] border-white/10 h-7" onClick={() => openPermissions(g.id)} data-testid={`edit-perms-${g.id}`}>
                  <Shield className="w-3 h-3 mr-1" /> Permisos
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Weekly Schedule */}
      <Card className="bg-card/50 border-white/5">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-400" /> Horarios de la Semana</CardTitle>
            <CardDescription className="text-xs">{week}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-black/20">
                <th className="p-3 text-[10px] font-semibold text-muted-foreground border-b border-white/5 w-40">ASESOR</th>
                {DAYS.map(d => (
                  <th key={d.key} className="p-3 text-[10px] font-semibold text-muted-foreground border-b border-white/5 text-center">{d.label.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {uniqueAgents.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No hay turnos asignados esta semana</td></tr>
              )}
              {uniqueAgents.map(agentId => (
                <tr key={agentId}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-400">{getMemberName(agentId).charAt(0)}</div>
                      <p className="text-xs font-medium text-white truncate max-w-[100px]">{getMemberName(agentId)}</p>
                    </div>
                  </td>
                  {DAYS.map(day => {
                    const dayShifts = getShiftsForSlot(agentId, day.key);
                    return (
                      <td key={day.key} className="p-1.5">
                        <div className="space-y-1">
                          {dayShifts.map(s => {
                            const isCustom = s.shift === 'custom' || s.time_start;
                            const shiftColor = s.shift === 'manana' ? 'indigo' : s.shift === 'tarde' ? 'amber' : 'blue';
                            const timeLabel = isCustom && s.time_start ? `${s.time_start}-${s.time_end}` : s.shift === 'manana' ? '9-14h' : '14-19h';
                            return (
                              <div key={s.id} className={`bg-${shiftColor}-500/10 border border-${shiftColor}-500/20 p-1.5 rounded-lg group relative`} data-testid={`shift-${s.id}`}>
                                <p className={`text-[9px] font-bold text-${shiftColor}-400`}>{timeLabel}</p>
                                <p className="text-[10px] text-white truncate">{getPropertyName(s.property_id)}</p>
                                {isAdmin && <button onClick={() => deleteShift(s.id)} className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 text-red-400"><X className="w-3 h-3" /></button>}
                              </div>
                            );
                          })}
                          {dayShifts.length === 0 && (
                            <div className="bg-black/20 border border-dashed border-white/10 p-1.5 rounded-lg h-10 flex items-center justify-center">
                              {isAdmin && <button onClick={() => { setForm({ ...form, agent_id: agentId, day: day.key }); setDialogOpen(true); }} className="text-[10px] text-muted-foreground hover:text-white">+</button>}
                            </div>
                          )}
                          {dayShifts.length > 0 && isAdmin && (
                            <button onClick={() => { setForm({ ...form, agent_id: agentId, day: day.key }); setDialogOpen(true); }} className="w-full text-[9px] text-muted-foreground hover:text-white text-center py-0.5 rounded border border-dashed border-white/5 hover:border-white/10">+ turno</button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Change Requests */}
      {changeRequests.length > 0 && isAdmin && (
        <Card className="bg-card/50 border-white/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              Solicitudes de Cambio <Badge className="bg-amber-500 text-black text-[9px]">{changeRequests.filter(r => r.status === 'pending').length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {changeRequests.filter(r => r.status === 'pending').map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`cr-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-white">{r.agent_name}</p>
                  <p className="text-xs text-muted-foreground">{r.original_day} {r.original_shift} → {r.requested_day} {r.requested_shift}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-500 hover:bg-emerald-500/10" onClick={() => handleChangeRequest(r.id, 'approve')}><Check className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-500/10" onClick={() => handleChangeRequest(r.id, 'reject')}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

        </TabsContent>

        {/* ── Ruleta de Asignacion Tab ────────────────── */}
        <TabsContent value="ruleta" className="space-y-6 mt-4">
          {rouletteLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-teal-400 animate-spin" /></div>
          ) : rouletteConfig ? (
            <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="roulette-config-card">
              <CardContent className="p-4 sm:p-6 space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2"><RefreshCw className="w-5 h-5 text-teal-400" />Ruleta de Asignacion</h3>
                    <p className="text-xs text-muted-foreground">Configura como se asignan automaticamente los leads a tu equipo</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setSimOpen(true)} variant="outline" className="border-white/10 text-xs" data-testid="sim-btn">
                      <Play className="w-3 h-3 mr-1" />Simulador
                    </Button>
                    <Button onClick={saveRouletteConfig} disabled={rouletteSaving} className="bg-teal-600 hover:bg-teal-500 text-xs" data-testid="save-roulette-btn">
                    {rouletteSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Guardar Ruleta
                  </Button>
                  </div>
                </div>

                {/* Mode */}
                <div>
                  <Label className="text-xs text-white/60">Modo de Asignacion</Label>
                  <Select value={rouletteConfig.mode} onValueChange={v => setRouletteConfig({...rouletteConfig, mode: v})}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="roulette-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="round_robin">Round Robin (turnos iguales)</SelectItem>
                      <SelectItem value="weighted">Ponderado (por pesos)</SelectItem>
                      <SelectItem value="rules_based">Basado en Reglas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Dedup & Spam */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                    <div><p className="text-xs text-white">Deduplicacion</p><p className="text-[9px] text-muted-foreground">Evita leads duplicados</p></div>
                    <Switch checked={rouletteConfig.dedup_enabled} onCheckedChange={v => setRouletteConfig({...rouletteConfig, dedup_enabled: v})} />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                    <div><p className="text-xs text-white">Deteccion Spam</p><p className="text-[9px] text-muted-foreground">Filtra leads sospechosos</p></div>
                    <Switch checked={rouletteConfig.spam_detection} onCheckedChange={v => setRouletteConfig({...rouletteConfig, spam_detection: v})} />
                  </div>
                </div>

                {rouletteConfig.spam_detection && (
                  <div>
                    <Label className="text-xs text-white/60">Palabras clave de spam</Label>
                    <Input value={(rouletteConfig.spam_keywords || []).join(', ')} onChange={e => setRouletteConfig({...rouletteConfig, spam_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="test, prueba, mystery" className="bg-black/20 border-white/10 text-xs" />
                  </div>
                )}

                {/* Auto-reassign */}
                <div>
                  <Label className="text-xs text-white/60">Tiempo de Reasignacion (segundos)</Label>
                  <Input type="number" value={rouletteConfig.auto_reassign_seconds} onChange={e => setRouletteConfig({...rouletteConfig, auto_reassign_seconds: +e.target.value})} className="bg-black/20 border-white/10 text-xs" />
                  <p className="text-[9px] text-muted-foreground mt-0.5">Si un agente no responde en este tiempo, el lead se reasigna</p>
                </div>

                {/* Zone Rules — solo si products NO está activo */}
                {!allowedModules.products && (
                <>
                <div className="space-y-2 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-white/60">Reglas por Zona</Label>
                    <Button variant="ghost" size="sm" onClick={() => setRouletteConfig({...rouletteConfig, zone_rules: [...(rouletteConfig.zone_rules || []), {zone: '', agents: []}]})} className="text-[10px] text-teal-400 h-6" data-testid="add-zone-rule">+ Zona</Button>
                  </div>
                  <p className="text-[9px] text-muted-foreground">Asigna leads de una zona o grupo de propiedades a agentes específicos.</p>
                  {(rouletteConfig.zone_rules || []).map((zr, i) => (
                    <div key={i} className="space-y-2 p-3 rounded bg-black/20 border border-white/5">
                      <Select value={zr.zone} onValueChange={v => { const r = [...rouletteConfig.zone_rules]; r[i] = {...r[i], zone: v}; setRouletteConfig({...rouletteConfig, zone_rules: r}); }}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-[10px] h-8"><SelectValue placeholder="Zona o grupo" /></SelectTrigger>
                        <SelectContent>
                          {[...(rouletteOptions.zones || []), ...(rouletteOptions.groups || [])].map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-2">
                        {(rouletteOptions.vendors || []).map(agent => (
                          <label key={agent.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${zr.agents?.includes(agent.id) ? 'bg-teal-500/10 border-teal-500/20 text-teal-300' : 'bg-black/20 border-white/10 text-white/50'}`}>
                            <input type="checkbox" checked={zr.agents?.includes(agent.id)} onChange={() => toggleRuleAgent(i, agent.id, 'zone_rules')} className="sr-only" />
                            <span>{agent.name}</span>
                          </label>
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setRouletteConfig({...rouletteConfig, zone_rules: rouletteConfig.zone_rules.filter((_, j) => j !== i)})} className="h-6 px-2 text-red-400"><Trash2 className="w-3 h-3 mr-1" />Quitar</Button>
                    </div>
                  ))}
                </div>

                {/* Property Type Rules */}
                <div className="space-y-2 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-white/60">Reglas por Tipo de Propiedad</Label>
                    <Button variant="ghost" size="sm" onClick={() => setRouletteConfig({...rouletteConfig, property_type_rules: [...(rouletteConfig.property_type_rules || []), {type: '', agents: []}]})} className="text-[10px] text-teal-400 h-6" data-testid="add-property-rule">+ Tipo</Button>
                  </div>
                  {(rouletteConfig.property_type_rules || []).map((pr, i) => (
                    <div key={i} className="space-y-2 p-3 rounded bg-black/20 border border-white/5">
                      <Select value={pr.type} onValueChange={v => { const r = [...rouletteConfig.property_type_rules]; r[i] = {...r[i], type: v}; setRouletteConfig({...rouletteConfig, property_type_rules: r}); }}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-[10px] h-7 flex-1"><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="casa">Casa</SelectItem>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="terreno">Terreno</SelectItem>
                          <SelectItem value="oficina">Oficina</SelectItem>
                          <SelectItem value="local">Local Comercial</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-2">
                        {(rouletteOptions.vendors || []).map(agent => (
                          <label key={agent.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${pr.agents?.includes(agent.id) ? 'bg-teal-500/10 border-teal-500/20 text-teal-300' : 'bg-black/20 border-white/10 text-white/50'}`}>
                            <input type="checkbox" checked={pr.agents?.includes(agent.id)} onChange={() => toggleRuleAgent(i, agent.id, 'property_type_rules')} className="sr-only" />
                            <span>{agent.name}</span>
                          </label>
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setRouletteConfig({...rouletteConfig, property_type_rules: rouletteConfig.property_type_rules.filter((_, j) => j !== i)})} className="h-6 px-2 text-red-400"><Trash2 className="w-3 h-3 mr-1" />Quitar</Button>
                    </div>
                  ))}
                </div>
                </>
                )}

                {/* Secciones jerárquicas — solo si brands o products están activos */}
                {allowedModules.products && (
                  <>
                    <HierarchicalGroupsSection
                      type="campaign"
                      title="Grupos por Fuente de Lead"
                      description="Cada fuente de lead puede tener su propio grupo de vendedores. Prioridad maxima en la asignacion."
                      entityLabel="Fuente"
                      entities={leadSources}
                      groups={rouletteGroups}
                      vendors={rouletteOptions.vendors || []}
                      onAdd={openGroupDialog}
                      onEdit={openGroupDialog}
                      onDelete={deleteGroup}
                      onToggle={toggleGroupActive}
                    />
                    <HierarchicalGroupsSection
                      type="product"
                      title="Grupos por Producto"
                      description="Todas las fuentes de un producto comparten este grupo si no tienen grupo propio."
                      entityLabel="Producto"
                      entities={products}
                      groups={rouletteGroups}
                      vendors={rouletteOptions.vendors || []}
                      onAdd={openGroupDialog}
                      onEdit={openGroupDialog}
                      onDelete={deleteGroup}
                      onToggle={toggleGroupActive}
                    />
                  </>
                )}

                {allowedModules.brands && (
                  <HierarchicalGroupsSection
                    type="brand"
                    title="Grupos por Marca"
                    description="Todos los productos de una marca comparten este grupo si no tienen grupo mas especifico."
                    entityLabel="Marca"
                    entities={brands}
                    groups={rouletteGroups}
                    vendors={rouletteOptions.vendors || []}
                    onAdd={openGroupDialog}
                    onEdit={openGroupDialog}
                    onDelete={deleteGroup}
                    onToggle={toggleGroupActive}
                  />
                )}

                {/* Carriles de Fuentes por Nivel de Rol */}
                {allowedModules.products && leadSources.length > 0 && (
                  <div className="space-y-3 pt-3 border-t border-white/5" data-testid="role-lanes">
                    <div>
                      <Label className="text-xs text-white/60">Fuentes de Lead por Nivel de Rol</Label>
                      <p className="text-[9px] text-muted-foreground mt-0.5">Cada fuente tiene un nivel de rol que determina en que carril de asignacion opera. Haz clic en el badge para cambiar de nivel.</p>
                    </div>
                    {['campaign', 'product', 'brand', 'client'].map(level => {
                      const levelSources = leadSources.filter(s => (s.role_level || 'campaign') === level);
                      if (level === 'product' && !allowedModules.products) return null;
                      if (level === 'brand' && !allowedModules.brands) return null;
                      const labels = { campaign: 'Rol de Campaña', product: 'Rol de Producto', brand: 'Rol de Marca', client: 'Rol de Cliente' };
                      const colors = { campaign: 'border-teal-500/20 bg-teal-500/5', product: 'border-emerald-500/20 bg-emerald-500/5', brand: 'border-violet-500/20 bg-violet-500/5', client: 'border-zinc-500/20 bg-zinc-500/5' };
                      return (
                        <div key={level} className={`p-3 rounded-lg border ${colors[level]}`} data-testid={`lane-${level}`}>
                          <p className="text-[10px] font-medium text-white/70 mb-2">{labels[level]}</p>
                          {levelSources.length === 0 ? (
                            <p className="text-[9px] text-muted-foreground italic">Sin fuentes en este nivel</p>
                          ) : (
                            <div className="space-y-1.5">
                              {levelSources.map(s => {
                                const group = rouletteGroups.find(g => g.type === level && g.linked_id === s.id);
                                return (
                                  <div key={s.id} className="flex items-center justify-between px-2 py-1.5 rounded bg-black/20 border border-white/5" data-testid={`lane-source-${s.id}`}>
                                    <div className="flex items-center gap-2">
                                      <p className="text-[10px] text-white">{s.name}</p>
                                      <Badge variant="outline" className="text-[7px] border-sky-500/20 text-sky-400">{s.channel}</Badge>
                                      <Select value={s.role_level || 'campaign'} onValueChange={v => changeRoleLevel(s.id, v)}>
                                        <SelectTrigger className="h-5 w-auto px-1.5 text-[8px] bg-transparent border-white/10 gap-0.5" data-testid={`role-badge-${s.id}`}>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="campaign">Campaña</SelectItem>
                                          {allowedModules.products && <SelectItem value="product">Producto</SelectItem>}
                                          {allowedModules.brands && <SelectItem value="brand">Marca</SelectItem>}
                                          <SelectItem value="client">Cliente</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-5 text-[8px] text-teal-400 px-1.5" onClick={() => group ? openGroupDialog(level, group) : openGroupDialog(level)} data-testid={`manage-role-${s.id}`}>
                                      {group ? 'Editar Rol' : '+ Crear Grupo'}
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Audit Log — Ultimos cambios */}
                {auditLogs.length > 0 && (
                  <div className="space-y-2 pt-3 border-t border-white/5" data-testid="audit-log-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-white/60 flex items-center gap-1.5"><History className="w-3 h-3" />Ultimos cambios</Label>
                    </div>
                    <div className="space-y-1">
                      {auditLogs.map((log, i) => (
                        <div key={log.id || i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-black/10 border border-white/[0.03]" data-testid={`audit-${i}`}>
                          <div className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                          <p className="text-[9px] text-white/70 flex-1">{log.details || log.action}</p>
                          <p className="text-[8px] text-muted-foreground shrink-0">{log.user_name}</p>
                          <p className="text-[8px] text-muted-foreground shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Weights */}
                {rouletteConfig.mode === 'weighted' && (
                  <div className="space-y-2 pt-3 border-t border-white/5">
                    <Label className="text-xs text-white/60">Pesos Personalizados (%)</Label>
                    <p className="text-[9px] text-muted-foreground">Define el porcentaje de leads que recibe cada agente</p>
                    <Input value={Object.entries(rouletteConfig.custom_weights || {}).map(([k,v]) => `${k}:${v}`).join(', ')}
                      onChange={e => { const w = {}; e.target.value.split(',').forEach(p => { const [k,v] = p.split(':').map(s => s.trim()); if (k && v) w[k] = +v; }); setRouletteConfig({...rouletteConfig, custom_weights: w}); }}
                      placeholder="agent_id:40, agent_id2:60" className="bg-black/20 border-white/10 text-[10px]" />
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-20 text-muted-foreground text-sm">Cargando configuracion...</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs remain outside tabs */}
      {/* Assign Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-guard-dialog">
          <DialogHeader><DialogTitle>Asignar Guardia</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Agente</Label>
              <Select value={form.agent_id} onValueChange={v => setForm({ ...form, agent_id: v })}>
                <SelectTrigger data-testid="guard-agent-select"><SelectValue placeholder="Seleccionar agente" /></SelectTrigger>
                <SelectContent>
                  {members.filter(m => ['admin', 'vendor', 'guard', 'manager'].includes(m.role)).map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name} ({etiquetaNivel(m.role)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Dia</Label>
                <Select value={form.day} onValueChange={v => setForm({ ...form, day: v })}>
                  <SelectTrigger data-testid="guard-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Turno</Label>
                <Select value={form.shift} onValueChange={v => {
                  const updates = { shift: v };
                  if (v === 'manana') { updates.time_start = '09:00'; updates.time_end = '14:00'; }
                  else if (v === 'tarde') { updates.time_start = '14:00'; updates.time_end = '19:00'; }
                  setForm({ ...form, ...updates });
                }}>
                  <SelectTrigger data-testid="guard-shift-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHIFTS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Custom time range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Hora Inicio</Label>
                <Input type="time" value={form.time_start} onChange={e => setForm({ ...form, time_start: e.target.value, shift: 'custom' })} className="bg-black/20 border-white/10" data-testid="guard-time-start" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Hora Fin</Label>
                <Input type="time" value={form.time_end} onChange={e => setForm({ ...form, time_end: e.target.value, shift: 'custom' })} className="bg-black/20 border-white/10" data-testid="guard-time-end" />
              </div>
            </div>
            {industry === 'real_estate' && (
            <div className="space-y-2">
              <Label>Propiedad (opcional)</Label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="guard-property-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Sin asignar</SelectItem>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="space-y-2">
              <Label>Replicar a otros días</Label>
              <div className="grid grid-cols-3 gap-2">
                {DAYS.filter(d => d.key !== form.day).map(d => (
                  <label key={d.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${form.replicate_days?.includes(d.key) ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300' : 'bg-black/20 border-white/10 text-white/50'}`}>
                    <input type="checkbox" checked={form.replicate_days?.includes(d.key)} onChange={() => setForm(f => ({ ...f, replicate_days: f.replicate_days?.includes(d.key) ? f.replicate_days.filter(x => x !== d.key) : [...(f.replicate_days || []), d.key] }))} className="sr-only" />
                    <span>{d.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={createShift} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="confirm-assign-btn">
              <Plus className="w-4 h-4 mr-2" /> Asignar Turno
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guard Permissions Dialog */}
      <Dialog open={permDialog} onOpenChange={setPermDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="guard-permissions-dialog">
          <DialogHeader><DialogTitle>Permisos de {permGuard?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {PERM_MODULES.map(mod => (
              <div key={mod.key} className="flex items-center justify-between p-2.5 rounded-lg bg-black/20 border border-white/5">
                <p className="text-xs text-white">{mod.label}</p>
                <Select value={permData[mod.key] || 'none'} onValueChange={v => setPermData({ ...permData, [mod.key]: v })}>
                  <SelectTrigger className="w-28 bg-black/30 border-white/10 text-xs h-7" data-testid={`perm-${mod.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin acceso</SelectItem>
                    <SelectItem value="read">Solo lectura</SelectItem>
                    <SelectItem value="both">Lectura/Escritura</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
            <Button onClick={savePermissions} className="w-full bg-amber-600 hover:bg-amber-500 text-white text-xs" data-testid="save-guard-perms-btn">
              Guardar Permisos
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="sm:max-w-md" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'} —{' '}
              {groupDialogType === 'campaign' ? 'Fuente de Lead' : groupDialogType === 'product' ? 'Producto' : 'Marca'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Nombre del grupo</Label>
              <Input
                value={groupForm.name}
                onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                placeholder="Ej: Grupo Facebook Leads"
                className="bg-black/20 border-white/10 text-xs"
                data-testid="group-name-input"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">
                {groupDialogType === 'campaign' ? 'Fuente de Lead vinculada' : groupDialogType === 'product' ? 'Producto vinculado' : 'Marca vinculada'}
              </Label>
              <Select value={groupForm.linked_id || '__none__'} onValueChange={v => setGroupForm({ ...groupForm, linked_id: v === '__none__' ? '' : v })}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="group-entity-select">
                  <SelectValue placeholder="Seleccionar (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin vincular</SelectItem>
                  {(groupDialogType === 'campaign' ? leadSources : groupDialogType === 'product' ? products : brands).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground">Si no vinculas una entidad, el grupo actua como fallback para ese nivel.</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Vendedores del grupo</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {(rouletteOptions.vendors || []).map(v => (
                  <label
                    key={v.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-[10px] cursor-pointer ${groupForm.vendor_ids.includes(v.id) ? 'bg-teal-500/10 border-teal-500/20 text-teal-300' : 'bg-black/20 border-white/10 text-white/50'}`}
                    data-testid={`vendor-toggle-${v.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={groupForm.vendor_ids.includes(v.id)}
                      onChange={() => setGroupForm(f => ({
                        ...f,
                        vendor_ids: f.vendor_ids.includes(v.id)
                          ? f.vendor_ids.filter(id => id !== v.id)
                          : [...f.vendor_ids, v.id]
                      }))}
                      className="sr-only"
                    />
                    <span>{v.name}</span>
                  </label>
                ))}
              </div>
              {(rouletteOptions.vendors || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground">No hay vendedores disponibles.</p>
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
              <div>
                <p className="text-xs text-white">Grupo activo</p>
                <p className="text-[9px] text-muted-foreground">Los grupos inactivos no reciben leads</p>
              </div>
              <Switch
                checked={groupForm.is_active}
                onCheckedChange={v => setGroupForm({ ...groupForm, is_active: v })}
                data-testid="group-active-switch"
              />
            </div>

            <Button onClick={saveGroup} disabled={groupSaving} className="w-full bg-teal-600 hover:bg-teal-500 text-xs" data-testid="save-group-btn">
              {groupSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              {editingGroup ? 'Guardar Cambios' : 'Crear Grupo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simulator Dialog */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="sm:max-w-md" data-testid="sim-dialog">
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><Play className="w-4 h-4 text-teal-400" />Simulador de Asignacion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-[9px] text-muted-foreground">Simula a que vendedor se asignaria un lead con estos parametros, sin crearlo.</p>
            {leadSources.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Fuente de Lead</Label>
                <Select value={simForm.lead_source_id || 'none'} onValueChange={v => { const src = leadSources.find(s => s.id === v); setSimForm({ ...simForm, lead_source_id: v === 'none' ? '' : v, product_id: src?.product_id || simForm.product_id, brand_id: src?.brand_id || simForm.brand_id }); }}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="sim-source"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ninguna</SelectItem>{leadSources.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {products.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Producto</Label>
                <Select value={simForm.product_id || 'none'} onValueChange={v => setSimForm({ ...simForm, product_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="sim-product"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ninguno</SelectItem>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {brands.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select value={simForm.brand_id || 'none'} onValueChange={v => setSimForm({ ...simForm, brand_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="sim-brand"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ninguna</SelectItem>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={runSimulation} disabled={simLoading} className="w-full bg-teal-600 hover:bg-teal-500 text-xs" data-testid="sim-run-btn">
              {simLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}Simular Asignacion
            </Button>
            {simResult && (
              <div className="space-y-2 pt-2 border-t border-white/5" data-testid="sim-result">
                <p className="text-xs text-white font-medium">Resultado: {simResult.assigned_vendor_name || 'Sin asignar'}</p>
                <div className="space-y-1">
                  {(simResult.trace || []).map((step, i) => {
                    const icon = step.matched ? '✓' : step.evaluated ? '✗' : '—';
                    const color = step.matched ? 'text-emerald-400' : step.evaluated ? 'text-red-400' : 'text-zinc-500';
                    return (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-black/20" data-testid={`sim-step-${step.level}`}>
                        <span className={`text-sm ${color}`}>{icon}</span>
                        <span className="text-[10px] text-white/70">{step.level}</span>
                        {step.rule_name && <span className="text-[9px] text-muted-foreground">— {step.rule_name}</span>}
                        {step.candidate_names?.length > 0 && <span className="text-[8px] text-sky-400 ml-auto">{step.candidate_names.join(', ')}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
